import React from 'react';
import { canUseBlacklist } from '../../constants/planFeatures';

// styling
import './Customer.css';

// constants
import {
  ADD_CUSTOMER_TOOLTIP,
  CUSTOMER_FIELDS,
  CUSTOMER_PAGES,
  CUSTOMER_TITLE,
} from './constants';

// custom components
import TopToolbar from '../global/subcomponents/topToolbar/TopToolbar';
import TwoStepDelete from '../global/subcomponents/twoStepDelete/TwoStepDelete';
import WriteGuard from '../global/ReadOnly/WriteGuard';

// mui components and icons
import { CircularProgress, IconButton, TableCell } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import EditIcon from '@material-ui/icons/Edit';
import TablePage from '../global/Table/TablePage';
import { Layout } from '../global/Table/TablePageHelpers/LayoutObject';
import { Recycling, Scoreboard, Verified } from '@mui/icons-material';
import { ROLE_ADMIN } from '../global/constants';
import RestoreIcon from '@mui/icons-material/Restore';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { Box, Tab, Tabs, Chip, TextField, InputAdornment } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import BlockIcon from '@mui/icons-material/Block';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { downloadCustomerFollowUpPdf } from './customerFollowUpPdf';

// ─── Tab IDs ───────────────────────────────────────────────────────────────────
const TAB_ACTIVE = 'active';
const TAB_INACTIVE = 'inactive';
const TAB_BLACKLIST = 'blacklist';

// ─── Roles allowed to delete a customer ──────────────────────────────────────
// Manager + gameroom owner only; NDS (ROLE_OWNER) is the platform super-user
// and already owns the Historical restore view, so it stays in the list.
const CUSTOMER_DELETE_ROLES = [
  'ROLE_MANAGER',
  'ROLE_MASTER_ADMIN',
  'ROLE_OWNER',
];

class CustomerTable extends React.Component {
  constructor(props) {
    const isIOSSafari = /iP(ad|hone|od).*Safari/i.test(navigator.userAgent);
    super(props);
    this.state = {
      isLoading: true,
      deleteId: null,
      search: '',
      isMobile: window.matchMedia('(max-width: 800px)').matches || isIOSSafari,
      showHistorical: false,
      activeTab: TAB_ACTIVE,

      sortBy: 'lastVisit',
      sortOrder: 'desc',
    };

    // TablePage keeps this in sync with whatever is on screen (current tab,
    // search, sort and page) so the PDF can export exactly that.
    this.visibleRowsRef = React.createRef();
    // Which tab the PDF should be labelled with — set during render.
    this.currentTabLabel = 'Active';
  }

  handleImageLoad = () => {
    this.setState({ isLoading: false });
  };

  handleImageError = () => {
    this.setState({ isLoading: false });
  };

  isOwner = () => {
    const { user } = this.props;
    const roles = user?.authority || [];
    return roles.includes('ROLE_OWNER');
  };

  // ─── Who may delete a customer ─────────────────────────────────────────────
  // Deleting archives the whole record — points, photos, visit history — so it
  // belongs to the people accountable for the gameroom: the Manager
  // (ROLE_MANAGER) and the Company Owner (ROLE_MASTER_ADMIN). Employees,
  // supervisors, reading guys and tech admins all carry ROLE_ADMIN and/or
  // ROLE_CUSTOMER as well, which is why the button can't key off those.
  // /customer/delete enforces the same list; this only keeps the affordance
  // off the screen.
  canDeleteCustomer = () => {
    const roles = this.props.user?.authority || [];
    return CUSTOMER_DELETE_ROLES.some((role) => roles.includes(role));
  };

  // ─── Partition all customers into 3 buckets in a SINGLE pass ────────────────
  // Blacklist takes priority: if isBlocked==true it goes there regardless of
  // isInactive. This keeps the logic clean and avoids double-counting.
  partitionCustomers = () => {
    const customers = this.props.customers || [];
    const active = [];
    const inactive = [];
    const blacklist = [];

    for (const c of customers) {
      if (c.isBlocked === true) {
        blacklist.push(c);
      } else if (c.isInactive === true) {
        inactive.push(c);
      } else {
        active.push(c);
      }
    }

    return { active, inactive, blacklist };
  };

  // Export the rows the user is currently looking at — same tab, same search,
  // same sort, same page, same "Results per page".
  handleDownloadPdf = () => {
    const view = this.visibleRowsRef.current || {};
    const rows = view.rows || [];

    if (rows.length === 0) {
      this.props.openFeedbackBar(
        'warning',
        'There are no customers to export.'
      );
      return;
    }

    downloadCustomerFollowUpPdf(rows, {
      locationName: this.props.user?.selectedLocationName,
      tabLabel: this.props.isHistorical ? 'Historical' : this.currentTabLabel,
      search: this.state.search,
      page: view.page ?? 0,
      rowsPerPage: view.rowsPerPage ?? rows.length,
      totalRows: view.totalRows ?? rows.length,
      paginated: view.paginated !== false,
    });
  };

  getContextualMenuItems = () => {
    const { isHistorical, showHistorical, hideHistorical } = this.props;
    const menuItems = [];

    if (!isHistorical) {
      menuItems.push({
        title: ADD_CUSTOMER_TOOLTIP,
        icon: <AddCircleIcon />,
        onClick: () =>
          this.props.setCurrentPage(CUSTOMER_PAGES.ADD_CUSTOMER, {}),
      });
    }

    menuItems.push({
      title: 'Download PDF',
      icon: <PictureAsPdfIcon />,
      onClick: this.handleDownloadPdf,
    });

    if (!isHistorical && this.isOwner()) {
      menuItems.push({
        title: 'Restore Customers',
        icon: <Recycling />,
        onClick: () => showHistorical(),
      });
    }

    if (isHistorical) {
      menuItems.push({
        title: 'Back',
        icon: <KeyboardBackspaceIcon />,
        onClick: () => hideHistorical(),
      });
    }

    return menuItems;
  };

  handleChange = (e, index) => {
    const updatedCustomers = [...this.props.customers];
    updatedCustomers[index][e.target.name] = e.target.value;
    this.setState({ customers: updatedCustomers });
  };

  getTableHeaders = () => {
    const headers = [];

    const isPrivileged =
      this.props.user?.routes?.includes('role-privilege') ||
      this.props.user?.authority?.includes(ROLE_ADMIN);

    headers.push(Layout.columnHead(null, null, null));
    headers.push(Layout.columnHead(null, null, null));

    if (!isPrivileged) {
      headers.push(Layout.columnHead(null, null, null));
    }

    const allowedFieldIds = [
      'files',
      'firstName',
      'lastName',
      'points',
      'lastVisit',
    ];

    const visibleFields = isPrivileged
      ? CUSTOMER_FIELDS
      : CUSTOMER_FIELDS.filter((field) => allowedFieldIds.includes(field.id));

    visibleFields.forEach((headCell) => {
      let sortId = headCell.id;
      if (sortId === 'lastVisit') {
        sortId = 'lastVisit_TIMESTAMP';
      } else if (sortId === 'registrationDate') {
        sortId = 'registrationDate_TIMESTAMP';
      }
      headers.push(Layout.columnHead(headCell.label, sortId));
    });

    headers.push(Layout.columnHead(null, null, null));

    return headers;
  };

  tableRow = (customer) => {
    const { isLoading } = this.state;
    const { isHistorical } = this.props;

    const isPrivileged =
      this.props.user?.routes?.includes('role-privilege') ||
      this.props.user?.authority?.includes(ROLE_ADMIN);

    return (
      <React.Fragment>
        {/* Manual points and Edit both open a form that writes. At a
            VIEW-ONLY location the buttons are removed but the cells stay,
            so the columns remain aligned with their headers. */}
        {!isHistorical && (
          <TableCell>
            <WriteGuard>
              <IconButton
                size="small"
                onClick={() => this.props.handleManualPointRow(customer)}
              >
                <Scoreboard />
              </IconButton>
            </WriteGuard>
          </TableCell>
        )}

        {/* Edit opens the customer PROFILE, which is a view. Left
            unguarded so a viewer can open it; the profile itself goes
            read-only and drops its save buttons. Manual points above
            stays hidden — that one writes immediately.
            See constants/readOnlyAccess.js. */}
        {!isHistorical && isPrivileged && (
          <TableCell>
            <IconButton
              size="small"
              onClick={() => this.props.handleEditRow(customer)}
            >
              <EditIcon />
            </IconButton>
          </TableCell>
        )}

        {isHistorical && (
          <>
            <TableCell>
              <WriteGuard>
                <IconButton
                  size="small"
                  onClick={() => {
                    this.props.restoreHistoricalCustomer(
                      { customerId: customer.customerId },
                      (message) => {
                        this.props.openFeedbackBar('success', message);
                        this.props.listAllHistoricalCustomers(
                          this.props.user.selectedLocationId
                        );
                      },
                      (message) => {
                        this.props.openFeedbackBar('error', message);
                      }
                    );
                  }}
                >
                  <RestoreIcon />
                </IconButton>
              </WriteGuard>
            </TableCell>

            <TableCell>
              <IconButton size="small" />
            </TableCell>
          </>
        )}

        <TableCell className="table-cell-left">
          {customer?.isBlocked === true ? (
            <ToggleOffIcon style={{ color: 'red', fontSize: '36px' }} />
          ) : customer?.isInactive === true ? (
            <ToggleOnIcon style={{ color: '#f59e0b', fontSize: '36px' }} />
          ) : (
            <ToggleOnIcon style={{ color: 'green', fontSize: '36px' }} />
          )}
        </TableCell>

        <TableCell className="table-cell-left">
          {isLoading && (
            <div className="loading-indicator">
              <CircularProgress />
            </div>
          )}
          <img
            src={customer?.capturedImageURL || customer?.files[0]?.localURL}
            alt="Customer"
            className="responsive-image-customer"
            onLoad={this.handleImageLoad}
            onError={this.handleImageError}
          />
        </TableCell>

        <TableCell className="table-cell-left">{customer?.firstName}</TableCell>
        <TableCell className="table-cell-left">{customer?.lastName}</TableCell>
        <TableCell className="table-cell-left">{customer?.points}</TableCell>

        {isPrivileged && (
          <TableCell className="table-cell-left">
            <span className="customer-phone-cell">
              {customer?.phone}
              {customer?.phoneVerified && (
                <Verified
                  titleAccess="Phone verified"
                  className="customer-verified-icon"
                />
              )}
            </span>
          </TableCell>
        )}

        {isPrivileged && (
          <TableCell className="table-cell-left">{customer?.dob}</TableCell>
        )}

        {isPrivileged && (
          <TableCell className="table-cell-left">
            {(() => {
              const regDate = customer?.registrationDate;
              if (!regDate) return '';
              const isoMatch = regDate.match(/^(\d{4})-(\d{2})-(\d{2})(.*)/);
              if (isoMatch) {
                const [, year, month, day, rest] = isoMatch;
                return `${month}/${day}/${year}${rest}`;
              }
              return regDate;
            })()}
          </TableCell>
        )}

        <TableCell className="table-cell-left">{customer?.lastVisit}</TableCell>

        {/* The delete column stays in place for everyone so the rows keep
            lining up with the trailing header cell; only the control inside
            it is withheld from anyone who isn't a manager or an owner. */}
        {!isHistorical &&
          (this.canDeleteCustomer() ? (
            <TwoStepDelete
              rowId={customer.customerId}
              handleDelete={() =>
                this.props.handleDeleteRow(customer.customerId)
              }
              deleteId={this.state.deleteId}
              setDeleteId={(deleteId) => this.setState({ deleteId })}
            />
          ) : (
            <TableCell />
          ))}
      </React.Fragment>
    );
  };

  searchStrings = (data) => {
    const firstName = data.firstName || '';
    const lastName = data.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return [firstName, lastName, fullName, data.phone];
  };

  parseDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return 0;
    const parts = dateStr.trim().split(/\s+/);
    const datePart = parts[0];
    const timePart = parts[1] || '00:00';
    const meridiem = parts[2] ? parts[2].toUpperCase() : 'AM';

    let [y, m, d] = [0, 0, 0];
    if (datePart.includes('/')) {
      const [mm, dd, yy] = datePart.split('/');
      y = parseInt(yy);
      m = parseInt(mm) - 1;
      d = parseInt(dd);
    } else {
      const [yy, mm, dd] = datePart.split('-');
      y = parseInt(yy);
      m = parseInt(mm) - 1;
      d = parseInt(dd);
    }

    let [h, min] = timePart.split(':').map((n) => parseInt(n));
    if (meridiem === 'PM' && h < 12) h += 12;
    if (meridiem === 'AM' && h === 12) h = 0;

    return new Date(y, m, d, h, min).getTime();
  };

  // ─── Inject sort-helper timestamps once, then partition ────────────────────
  getSortableCustomers = () =>
    (this.props.customers || []).map((customer) => ({
      ...customer,
      lastVisit_TIMESTAMP: this.parseDate(customer.lastVisit),
      registrationDate_TIMESTAMP: this.parseDate(customer.registrationDate),
    }));

  applySearchFilter = (customers) => {
    const { search } = this.state;
    if (!search) return customers;
    const term = search.toLowerCase();
    return customers.filter((c) => {
      const fields = this.searchStrings(c);
      return fields.some(
        (text) => text && text.toString().toLowerCase().includes(term)
      );
    });
  };

  render() {
    const { activeTab, search } = this.state;
    const { isHistorical, user } = this.props;

    // The Active / Inactive split is part of EVERY plan now, so those two
    // tabs always render. Basic used to have no tabs at all and folded
    // inactive customers into one undifferentiated list.
    //
    // The server side of this matters as much as the tabs:
    // CustomerService.inActiveCustomersJob used to skip entry-tier
    // companies, so nothing at a Basic company was ever flagged inactive.
    // Showing the tab without that change would have given Basic a feature
    // that could never populate.
    //
    // Blacklist stays paid-tier — it is a separate line item — with the
    // long-standing exception that a Basic company still sees it when the
    // user carries ROLE_SECURITY (the security-dashboard capability).
    const showBlacklistTab = canUseBlacklist(user);

    // Build sortable list, apply search across ALL customers, then partition
    const sortable = this.getSortableCustomers();
    const filtered = this.applySearchFilter(sortable);

    const active = [];
    const inactive = [];
    const blacklist = [];

    for (const c of filtered) {
      if (c.isBlocked === true) {
        blacklist.push(c);
      } else if (c.isInactive === true) {
        inactive.push(c);
      } else {
        active.push(c);
      }
    }

    // Determine effective tab. Blacklist can be hidden, so a stored
    // selection of it has to fall back rather than render an empty pane.
    let effectiveTab =
      activeTab === TAB_BLACKLIST && !showBlacklistTab ? TAB_ACTIVE : activeTab;

    // Auto-switch to a tab that has results when searching
    if (search) {
      const currentRows =
        effectiveTab === TAB_ACTIVE
          ? active
          : effectiveTab === TAB_INACTIVE
          ? inactive
          : blacklist;

      if (currentRows.length === 0) {
        if (active.length > 0) {
          effectiveTab = TAB_ACTIVE;
        } else if (inactive.length > 0) {
          effectiveTab = TAB_INACTIVE;
        } else if (blacklist.length > 0 && showBlacklistTab) {
          effectiveTab = TAB_BLACKLIST;
        }
      }
    }

    // Which dataset is currently visible. Active no longer absorbs the
    // inactive rows on Basic — that fold-in existed only because Basic had
    // no Inactive tab to send them to.
    const visibleRows =
      effectiveTab === TAB_ACTIVE
        ? active
        : effectiveTab === TAB_INACTIVE
        ? inactive
        : blacklist;

    // Remember which list is on screen so the PDF can label itself correctly.
    this.currentTabLabel =
      effectiveTab === TAB_ACTIVE
        ? 'Active'
        : effectiveTab === TAB_INACTIVE
        ? 'Inactive'
        : 'Blacklist';

    // Tab label helpers
    const tabLabel = (icon, label, count, color) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
        {icon}
        <span>{label}</span>
        <Chip
          label={count}
          size="small"
          sx={{
            bgcolor: color,
            color: 'white',
            fontWeight: 800,
            fontSize: '0.7rem',
            height: 20,
            minWidth: 24,
          }}
        />
      </Box>
    );

    return (
      <>
        <TopToolbar
          pageName={isHistorical ? 'Historical Customers' : CUSTOMER_TITLE}
          menuItems={this.getContextualMenuItems()}
        />

        {/* ── Search bar ── */}
        {!isHistorical && (
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5, bgcolor: '#fafafa' }}>
            <TextField
              placeholder="Search by name or phone..."
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => this.setState({ search: e.target.value })}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ fontSize: 20, color: '#999' }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => this.setState({ search: '' })}
                    >
                      <ClearIcon style={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
        )}

        {/* ── Tab bar (only shown on the live customer view) ── */}
        {!isHistorical && (
          <Box
            sx={{
              borderBottom: '2px solid #e0e0e0',
              bgcolor: '#fafafa',
              px: 2,
              position: 'sticky',
              // Pins directly under the toolbar. The literal 131 here was the
              // portrait-phone value (75 + MUI Toolbar 56) and sat 8px under
              // the 64px desktop toolbar, which painted over it.
              top: 'var(--nds-sticky-top-2)',
              zIndex: 19,
            }}
          >
            <Tabs
              value={effectiveTab}
              onChange={(_, v) => this.setState({ activeTab: v })}
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  textTransform: 'none',
                  minHeight: 48,
                  color: '#666',
                },
                '& .Mui-selected': { color: '#1e2b58 !important' },
                '& .MuiTabs-indicator': {
                  bgcolor: '#1e2b58',
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                },
              }}
            >
              <Tab
                value={TAB_ACTIVE}
                label={tabLabel(
                  <PeopleIcon sx={{ fontSize: 18 }} />,
                  'Active',
                  active.length,
                  '#2e7d32'
                )}
              />
              <Tab
                value={TAB_INACTIVE}
                label={tabLabel(
                  <PersonOffIcon sx={{ fontSize: 18 }} />,
                  'Inactive',
                  inactive.length,
                  '#e65100'
                )}
              />
              {showBlacklistTab && (
                <Tab
                  value={TAB_BLACKLIST}
                  label={tabLabel(
                    <BlockIcon sx={{ fontSize: 18 }} />,
                    'Blacklist',
                    blacklist.length,
                    '#b71c1c'
                  )}
                />
              )}
            </Tabs>
          </Box>
        )}

        {/* ── Table ── */}
        <TablePage
          tableName="CustomerTable"
          tableRows={visibleRows}
          tableColumnHeads={this.getTableHeaders()}
          tableRow={this.tableRow}
          paginationText="Results"
          hideAllRowsOption
          persistRowsPerPage={false}
          defaultRowsPerPage={50}
          defaultSortBy="lastVisit_TIMESTAMP"
          visibleRowsRef={this.visibleRowsRef}
        />
      </>
    );
  }
}

export default CustomerTable;
